<template>
    <div class="max-w-6xl mx-auto pb-8 space-y-6">
        <Loading :overlay="true" overlay-text="gerando relatório..." :show="loadingReport" />
        <ConclusionGenerationOverlay :show="Boolean(loadingConclusionRecordUuid)" />

        <div class="hidden space-y-2 sm:block">
            <h1 class="text-2xl font-bold text-gray-800">Prontuário</h1>
            <p class="text-sm text-gray-500">Dados do paciente, conclusão IA da avaliação mais recente, histórico clínico, matriz de risco e arquivos no mesmo lugar.</p>
        </div>

        <template v-if="loading">
            <section class="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
                <div class="px-6 py-6">
                    <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                        <div class="flex items-start gap-4">
                            <div class="skeleton h-20 w-20 rounded-3xl shrink-0"></div>
                            <div class="min-w-0 space-y-3">
                                <div class="skeleton h-7 w-56 rounded-xl"></div>
                                <div class="skeleton h-4 w-44 rounded"></div>
                                <div class="flex gap-2">
                                    <div class="skeleton h-7 w-20 rounded-full"></div>
                                    <div class="skeleton h-7 w-24 rounded-full"></div>
                                </div>
                            </div>
                        </div>
                        <div class="flex gap-2">
                            <div class="skeleton h-10 w-32 rounded-xl"></div>
                            <div class="skeleton h-10 w-36 rounded-xl"></div>
                        </div>
                    </div>
                </div>

                <div class="grid gap-6 border-t border-slate-100 px-6 py-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
                    <div class="grid gap-4 sm:grid-cols-2">
                        <div v-for="i in 6" :key="`patient-main-${i}`" class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="skeleton h-3 w-24 rounded"></div>
                            <div class="mt-3 skeleton h-4 w-32 rounded"></div>
                        </div>
                    </div>

                    <div class="grid gap-4">
                        <div v-for="i in 3" :key="`patient-side-${i}`" class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="skeleton h-3 w-20 rounded"></div>
                            <div class="mt-3 skeleton h-4 w-28 rounded"></div>
                            <div class="mt-2 skeleton h-3 w-16 rounded"></div>
                        </div>
                    </div>
                </div>

                <div class="grid gap-4 border-t border-slate-100 px-6 py-6 lg:grid-cols-2">
                    <div v-for="i in 5" :key="`patient-text-${i}`" class="rounded-2xl border border-slate-100 px-4 py-4" :class="{ 'lg:col-span-2': i === 5 }">
                        <div class="skeleton h-3 w-28 rounded"></div>
                        <div class="mt-3 space-y-2">
                            <div class="skeleton h-4 w-full rounded"></div>
                            <div class="skeleton h-4 w-5/6 rounded"></div>
                            <div class="skeleton h-4 w-2/3 rounded"></div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="flex items-center justify-between">
                    <div class="skeleton h-6 w-32 rounded"></div>
                    <div class="skeleton h-10 w-32 rounded-xl"></div>
                </div>
                <div class="mt-2 skeleton h-4 w-80 rounded"></div>
                <div class="mt-6 grid gap-4 lg:grid-cols-2">
                    <div v-for="i in 2" :key="`acias-page-skeleton-${i}`" class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                        <div class="flex items-center justify-between">
                            <div class="skeleton h-4 w-40 rounded"></div>
                            <div class="skeleton h-6 w-20 rounded-full"></div>
                        </div>
                        <div class="mt-4 space-y-2">
                            <div class="skeleton h-3 w-full rounded"></div>
                            <div class="skeleton h-3 w-5/6 rounded"></div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="skeleton h-6 w-40 rounded"></div>
                <div class="mt-2 skeleton h-4 w-72 rounded"></div>
                <div class="mt-6 flex justify-center overflow-hidden">
                    <div class="skeleton h-[320px] w-full max-w-4xl rounded-[24px]"></div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <div class="skeleton h-6 w-24 rounded"></div>
                        <div class="mt-2 skeleton h-4 w-80 rounded"></div>
                    </div>
                    <div class="skeleton h-28 w-full max-w-md rounded-[24px]"></div>
                </div>

                <div class="mt-6 space-y-4">
                    <div v-for="i in 3" :key="`file-skeleton-${i}`" class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5 shadow-sm">
                        <div class="flex items-start gap-4">
                            <div class="skeleton h-12 w-12 rounded-2xl shrink-0"></div>
                            <div class="flex-1 min-w-0">
                                <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div class="min-w-0 flex-1 space-y-2">
                                        <div class="skeleton h-4 w-2/3 rounded"></div>
                                        <div class="skeleton h-3 w-1/3 rounded"></div>
                                        <div class="skeleton h-3 w-1/2 rounded"></div>
                                    </div>
                                    <div class="flex gap-2 shrink-0">
                                        <div class="skeleton h-9 w-20 rounded-xl"></div>
                                        <div class="skeleton h-9 w-20 rounded-xl"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="skeleton h-6 w-28 rounded"></div>
                <div class="mt-2 skeleton h-4 w-80 rounded"></div>

                <div class="mt-6 grid gap-4 lg:grid-cols-2">
                    <div class="rounded-[24px] border border-slate-100 p-5">
                        <div class="skeleton h-4 w-44 rounded"></div>
                        <div class="mt-3 space-y-2">
                            <div class="skeleton h-3 w-full rounded"></div>
                            <div class="skeleton h-3 w-5/6 rounded"></div>
                            <div class="skeleton h-3 w-3/4 rounded"></div>
                        </div>
                    </div>
                    <div class="rounded-[24px] border border-slate-100 p-5">
                        <div class="skeleton h-4 w-32 rounded"></div>
                        <div class="mt-4 space-y-3">
                            <div class="skeleton h-10 w-full rounded-xl"></div>
                            <div class="skeleton h-10 w-full rounded-xl"></div>
                            <div class="skeleton h-10 w-full rounded-xl"></div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="skeleton h-6 w-28 rounded"></div>
                <div class="mt-2 skeleton h-4 w-72 rounded"></div>

                <div class="mt-6 space-y-3">
                    <div v-for="i in 4" :key="`record-skeleton-${i}`" class="rounded-[24px] border border-slate-100 bg-white p-4 shadow-sm">
                        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div class="space-y-2">
                                <div class="skeleton h-4 w-40 rounded"></div>
                                <div class="skeleton h-3 w-32 rounded"></div>
                            </div>
                            <div class="grid gap-3 sm:grid-cols-3 md:min-w-[420px]">
                                <div class="space-y-2">
                                    <div class="skeleton h-3 w-20 rounded"></div>
                                    <div class="skeleton h-4 w-24 rounded"></div>
                                </div>
                                <div class="space-y-2">
                                    <div class="skeleton h-3 w-24 rounded"></div>
                                    <div class="skeleton h-4 w-28 rounded"></div>
                                </div>
                                <div class="flex items-center sm:justify-end gap-2">
                                    <div class="skeleton h-9 w-20 rounded-xl"></div>
                                    <div class="skeleton h-9 w-16 rounded-xl"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </template>

        <section id="patient-section" v-else class="scroll-mt-6 overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
            <div class="border-b border-slate-100 bg-[radial-gradient(circle_at_top_left,_rgba(236,254,255,0.95),_rgba(248,250,252,0.98)_42%,_rgba(255,255,255,1))] px-4 py-5 sm:px-6 sm:py-6">
                <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
                        <div v-if="patient.photo_url" class="h-20 w-20 shrink-0 overflow-hidden rounded-3xl ring-4 ring-white shadow-sm">
                            <img :src="patient.photo_url" alt="" class="h-full w-full object-cover" />
                        </div>
                        <div v-else class="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-slate-200 text-2xl font-bold text-slate-500 ring-4 ring-white shadow-sm">
                            {{ initials(patient.name) }}
                        </div>

                        <div class="min-w-0 flex-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h2 class="break-words text-2xl font-bold text-slate-800">{{ patient.name }}</h2>
                                <span class="rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                                    {{ levelLabel(patient.level) }}
                                </span>
                            </div>
                            <p class="mt-1 break-words text-sm text-slate-500">{{ patient.email || 'Sem email cadastrado' }}</p>
                            <div class="mt-3 flex flex-wrap gap-2">
                                <span
                                    v-for="modality in modalityLabels(patient)"
                                    :key="modality"
                                    class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"
                                >
                                    {{ modality }}
                                </span>
                                <span v-if="!modalityLabels(patient).length" class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-400">
                                    Sem modalidade
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="grid gap-2 sm:flex sm:flex-wrap">
                        <router-link
                            :to="{ name: 'patientForm', params: { uuid: patient.uuid } }"
                            class="btn btn-sm btn-primary-outline w-full sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mt-1 h-4 w-4 text-cyan-600">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                            </svg>
                            Editar paciente
                        </router-link>

                        <router-link
                            v-if="patient.uuid"
                            :to="{ name: 'avaliation', params: { patient_uuid: patient.uuid } }"
                            class="btn btn-primary order-first min-h-12 w-full sm:order-none sm:min-h-0 sm:w-auto sm:px-2 sm:py-1"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mt-1 h-5 w-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
                            </svg>
                            Nova avaliação
                        </router-link>
                        <button
                            type="button"
                            class="btn btn-sm w-full border-rose-200 bg-white text-rose-600 hover:border-rose-300 hover:bg-rose-50 sm:w-auto"
                            :disabled="deletingPatient"
                            @click="openDeleteModal"
                        >
                            Excluir paciente
                        </button>
                    </div>
                </div>
            </div>

            <details class="group border-t border-slate-100">
                <summary class="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-5 sm:px-6">
                    <div>
                        <div class="text-sm font-semibold text-slate-800">Detalhes do paciente</div>
                        <div class="mt-1 text-xs text-slate-400">Telefone, documento, nascimento, endereço e anotações clínicas.</div>
                    </div>
                    <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-500 transition-transform duration-200 group-open:rotate-180">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </summary>

                <div class="grid gap-4 px-4 pb-6 sm:px-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)] lg:gap-6">
                    <div class="grid gap-3 sm:grid-cols-2 sm:gap-4">
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Telefone</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.phone || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Documento</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.document || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Cidade / Estado</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.city ? patient.city.name : '—' }}<span v-if="patient.state"> / {{ patient.state.name }}</span></div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Endereço</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.address || '—' }}<span v-if="patient.zipcode"> · {{ patient.zipcode }}</span></div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Médico</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.doctor || '—' }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Profissão</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.profession || '—' }}</div>
                        </div>
                    </div>

                    <div class="grid gap-3 sm:gap-4">
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Nascimento</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ patient.birthdate ? defaultDate(patient.birthdate) : '—' }}</div>
                            <div v-if="patient.birthdate" class="mt-1 text-xs text-slate-400">{{ calculateAge(patient.birthdate) }} anos</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Sexo</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ genderLabel(patient.gender) }}</div>
                        </div>
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Cadastro</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ formatDate(patient.created_at) }}</div>
                            <div class="mt-1 text-xs text-slate-400">{{ formatRelative(patient.created_at) }}</div>
                        </div>
                    </div>
                </div>

                <div class="grid gap-3 border-t border-slate-100 px-4 pb-6 pt-6 sm:px-6 sm:gap-4 lg:grid-cols-2">
                    <div class="rounded-2xl border border-slate-100 px-4 py-4">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Queixa principal</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.chief_complaint ? patient.chief_complaint.toString() : 'Sem observações registradas.' }}</div>
                    </div>
                    <div class="rounded-2xl border border-slate-100 px-4 py-4">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">HDA</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.history_current_illness || 'Sem observações registradas.' }}</div>
                    </div>
                    <div class="rounded-2xl border border-slate-100 px-4 py-4 lg:col-span-2">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">História pregressa</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.history_past_illness || 'Sem observações registradas.' }}</div>
                    </div>
                    <div class="rounded-2xl border border-slate-100 px-4 py-4">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Doenças pré-existentes</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.pre_existing_disease || 'Sem observações registradas.' }}</div>
                    </div>
                    <div class="rounded-2xl border border-slate-100 px-4 py-4">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Fatores psicossociais</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.psychosocial_factors || 'Sem observações registradas.' }}</div>
                    </div>
                    <div class="rounded-2xl border border-slate-100 px-4 py-4 lg:col-span-2">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Objetivos e contextos</div>
                        <div class="mt-2 text-sm leading-6 text-slate-600">{{ patient.context || 'Sem observações registradas.' }}</div>
                    </div>
                </div>
            </details>
        </section>

        <nav v-if="!loading" class="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" aria-label="Atalhos do prontuário">
            <button
                v-for="shortcut in RECORD_SHORTCUTS"
                :key="shortcut.id"
                type="button"
                class="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-cyan-300 hover:bg-cyan-50/50 hover:shadow-md"
                @click="scrollToSection(shortcut.id)"
            >
                <span class="block text-xs font-black text-slate-700">{{ shortcut.label }}</span>
                <span class="mt-1 block text-[11px] font-medium text-slate-400">{{ shortcut.description }}</span>
            </button>
        </nav>

        <div id="acias-section" class="scroll-mt-6">
            <AciasSection v-if="!loading && patient.uuid" :patient="patient" />
        </div>

        <section id="conclusion-section" v-if="!loading" class="scroll-mt-6 rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
            <div class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h2 class="text-xl font-bold text-gray-800">Conclusão</h2>
                    <p class="mt-1 text-sm text-gray-500">Prévia da conclusão IA da avaliação mais recente — o conteúdo completo fica na página dedicada.</p>
                </div>

                <button
                    v-if="!loadingConclusion && recentRecord && !latestConclusion"
                    type="button"
                    class="btn btn-sm w-full gap-2 sm:w-auto"
                    :class="conclusionSummaryButtonClass(recentRecord)"
                    :disabled="loadingConclusionRecordUuid === recentRecord.uuid"
                    @click="handleConclusionAction(recentRecord)"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4" :class="{ 'record-ai-cta-icon': shouldHighlightConclusion(recentRecord) }">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                    </svg>
                    {{ loadingConclusionRecordUuid === recentRecord.uuid ? 'Gerando...' : recordConclusions[recentRecord.uuid] ? 'Acessar conclusão' : 'Gerar conclusão com IA' }}
                </button>
            </div>

            <div v-if="loadingConclusion" class="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
                <div class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                    <div class="skeleton h-11 w-full max-w-xs rounded-2xl"></div>
                    <div class="mt-4 space-y-2">
                        <div class="skeleton h-3 w-full rounded"></div>
                        <div class="skeleton h-3 w-full rounded"></div>
                        <div class="skeleton h-3 w-5/6 rounded"></div>
                    </div>
                    <div class="mt-3 skeleton h-4 w-48 rounded"></div>
                </div>

                <div class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                    <div class="skeleton h-4 w-28 rounded"></div>
                    <div class="mt-4 space-y-2">
                        <div class="skeleton h-3 w-full rounded"></div>
                        <div class="skeleton h-3 w-full rounded"></div>
                        <div class="skeleton h-3 w-full rounded"></div>
                        <div class="skeleton h-3 w-full rounded"></div>
                    </div>
                </div>
            </div>

            <div v-else-if="!recentRecord" class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-6 text-sm text-slate-500">
                Ainda não existe nenhuma avaliação para este paciente. Salve uma avaliação para gerar a conclusão com IA.
            </div>

            <div v-else-if="latestConclusion" class="space-y-5">
                <div class="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
                    <div class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                        <div class="rounded-2xl border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm text-cyan-900">
                            Esta conclusão pertence à avaliação de {{ formatDate(recentRecord.created_at) }}.
                        </div>

                        <p class="mt-4 text-justify text-sm leading-7 text-slate-600">{{ conclusionPreview }}</p>

                        <button type="button" class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 hover:text-cyan-700" @click="goToConclusion(recentRecord)">
                            Continuar lendo na página dedicada
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </button>
                    </div>

                    <div class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                        <div class="text-sm font-semibold text-slate-800">Resumo visual</div>
                        <div class="mt-4 space-y-4">
                            <div>
                                <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Perfil de risco</div>
                                <div class="space-y-2">
                                    <div v-for="item in riskProfileChart" :key="item.label" class="grid grid-cols-[90px_1fr_28px] items-center gap-3">
                                        <div class="text-xs font-semibold text-slate-500">{{ item.label }}</div>
                                        <div class="h-2.5 overflow-hidden rounded-full bg-slate-200">
                                            <div class="h-full rounded-full" :style="{ width: `${item.width}%`, backgroundColor: item.color }"></div>
                                        </div>
                                        <div class="text-right text-xs font-semibold text-slate-500">{{ item.value }}</div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Probabilidade</div>
                                <div class="grid grid-cols-2 gap-2">
                                    <div v-for="item in probabilityChart" :key="item.label" class="rounded-2xl bg-white px-3 py-3 shadow-sm ring-1 ring-slate-100">
                                        <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{{ item.label }}</div>
                                        <div class="mt-1 text-lg font-bold text-slate-700">{{ item.value }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid gap-2 sm:flex sm:flex-wrap">
                    <button type="button" class="btn btn-sm btn-primary-outline w-full sm:w-auto" @click="goToAvaliation(recentRecord)">Abrir avaliação recente</button>
                    <button type="button" class="btn btn-sm btn-primary w-full sm:w-auto" @click="goToConclusion(recentRecord)">Ver conclusão completa</button>
                    <button type="button" class="btn btn-sm btn-primary w-full sm:w-auto" :disabled="exportingConclusionPdf" @click="exportConclusionPdf">
                        {{ exportingConclusionPdf ? 'Exportando...' : 'Exportar PDF' }}
                    </button>
                </div>
            </div>
        </section>

        <section id="records-section" v-if="!loading" class="scroll-mt-6 rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
            <div class="mb-5">
                <h2 class="text-xl font-bold text-gray-800">Avaliações</h2>
                <p class="mt-1 text-sm text-gray-500">Histórico do paciente com o mesmo padrão da listagem geral.</p>
            </div>

            <div v-if="records.length" class="space-y-3">
                <div
                    v-for="record in records"
                    :key="record.id"
                    class="cursor-pointer rounded-[24px] border bg-white p-4 shadow-sm transition-colors duration-200"
                    :class="recordCardClass(record)"
                    @click="goToAvaliation(record)"
                >
                    <div class="space-y-4 md:hidden">
                        <div class="min-w-0">
                            <div class="text-sm font-semibold text-slate-800">Avaliação de {{ patient.name }}</div>
                            <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                                <span
                                    class="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                                    :class="matrixBadgeClass(record)"
                                >
                                    {{ matrixBadgeLabel(record) }}
                                </span>
                                <span
                                    v-if="recordConclusions[record.uuid] && recordConclusions[record.uuid].status === 'done'"
                                    class="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700"
                                >
                                    Conclusão disponível
                                </span>
                            </div>
                        </div>

                        <div class="grid gap-3 sm:grid-cols-2">
                            <div class="rounded-2xl bg-slate-50 px-4 py-3">
                                <div class="text-[10px] uppercase tracking-wide text-gray-400">Criada em</div>
                                <div class="mt-0.5 text-sm text-gray-700">{{ formatDate(record.created_at) }}</div>
                                <div class="text-xs text-gray-400">{{ formatRelative(record.created_at) }}</div>
                            </div>
                            <div class="rounded-2xl bg-slate-50 px-4 py-3">
                                <div class="text-[10px] uppercase tracking-wide text-gray-400">Última atualização</div>
                                <div class="mt-0.5 text-sm text-gray-700">{{ formatDateTime(record.updated_at) }}</div>
                                <div class="text-xs text-gray-400">{{ formatRelative(record.updated_at) }}</div>
                            </div>
                            <div class="rounded-2xl bg-slate-50 px-4 py-3 sm:col-span-2">
                                <div class="mb-2 text-[10px] uppercase tracking-wide text-gray-400">Ações</div>
                                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2" @click.stop>
                                    <button
                                        type="button"
                                        class="btn btn-sm w-full gap-2"
                                        :class="conclusionActionButtonClass(record)"
                                        :disabled="loadingConclusionRecordUuid === record.uuid"
                                        @click.stop="handleConclusionAction(record)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4" :class="{ 'record-ai-cta-icon': shouldHighlightConclusion(record) }">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                        </svg>
                                        {{ loadingConclusionRecordUuid === record.uuid ? 'Gerando...' : recordConclusions[record.uuid] ? 'Acessar conclusão' : 'Gerar conclusão com IA' }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm w-full"
                                        :class="matrixActionButtonClass(record)"
                                        @click.stop="openMatrix(record)"
                                    >
                                        {{ matrixActionLabel(record) }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-primary w-full sm:col-span-2"
                                        :disabled="loadingReport"
                                        @click.stop="openReportBuilder(record.id)"
                                    >
                                        PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="hidden md:flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div class="min-w-0">
                            <div class="text-sm font-semibold text-slate-800">Avaliação de {{ patient.name }}</div>
                            <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                                <span
                                    class="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                                    :class="matrixBadgeClass(record)"
                                >
                                    {{ matrixBadgeLabel(record) }}
                                </span>
                                <span
                                    v-if="recordConclusions[record.uuid] && recordConclusions[record.uuid].status === 'done'"
                                    class="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700"
                                >
                                    Conclusão disponível
                                </span>
                            </div>
                        </div>

                        <div class="grid gap-3 md:grid-cols-3 lg:min-w-[420px] lg:max-w-[720px] lg:flex-1 lg:items-center">
                            <div>
                                <div class="text-[10px] uppercase tracking-wide text-gray-400">Criada em</div>
                                <div class="mt-0.5 text-sm text-gray-700">{{ formatDate(record.created_at) }}</div>
                                <div class="text-xs text-gray-400">{{ formatRelative(record.created_at) }}</div>
                            </div>
                            <div>
                                <div class="text-[10px] uppercase tracking-wide text-gray-400">Última atualização</div>
                                <div class="mt-0.5 text-sm text-gray-700">{{ formatDateTime(record.updated_at) }}</div>
                                <div class="text-xs text-gray-400">{{ formatRelative(record.updated_at) }}</div>
                            </div>
                            <div class="flex items-center lg:justify-end">
                                <div class="flex flex-wrap gap-2 lg:justify-end" @click.stop>
                                    <button
                                        type="button"
                                        class="btn btn-sm gap-2"
                                        :class="conclusionActionButtonClass(record)"
                                        :disabled="loadingConclusionRecordUuid === record.uuid"
                                        @click.stop="handleConclusionAction(record)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4" :class="{ 'record-ai-cta-icon': shouldHighlightConclusion(record) }">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                        </svg>
                                        {{ loadingConclusionRecordUuid === record.uuid ? 'Gerando...' : recordConclusions[record.uuid] ? 'Acessar conclusão' : 'Gerar conclusão com IA' }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm"
                                        :class="matrixActionButtonClass(record)"
                                        @click.stop="openMatrix(record)"
                                    >
                                        {{ matrixActionLabel(record) }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-primary"
                                        :disabled="loadingReport"
                                        @click.stop="openReportBuilder(record.id)"
                                    >
                                        PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400">
                Nenhuma avaliação encontrada para este paciente.
            </div>

            <PaginationV2 v-show="records.length && !loading" :total="totalRecords" :limit="limit" :current="page" item-label="avaliação" @changePage="changePagination"></PaginationV2>
        </section>

        <section id="risk-matrix-section" v-if="!loading" class="scroll-mt-6 rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
            <div class="mb-5 flex items-center justify-between gap-3">
                <div>
                    <h2 class="text-xl font-bold text-gray-800">Matriz de Risco</h2>
                    <p class="mt-1 text-sm text-gray-500">Visual da matriz mais recente vinculada ao paciente.</p>
                </div>
            </div>

            <div v-if="matrixLoading" class="flex justify-center overflow-hidden">
                <div class="skeleton h-[320px] w-full max-w-4xl rounded-[24px]"></div>
            </div>

            <div v-else-if="matrix && matrix.length" class="flex justify-center">
                <div id="matrix-container" class="max-w-full bg-white">
                    <div class="flex flex-col items-center gap-6 p-1 sm:p-2 lg:flex-row lg:items-start">
                        <div class="sm:hidden">
                            <RiskMatrix :data="matrix" :compact="true"></RiskMatrix>
                        </div>
                        <div class="hidden sm:block">
                            <RiskMatrix :data="matrix"></RiskMatrix>
                        </div>
                        <BodyRiskMap v-if="showBodyRiskMap" :data="matrix" :width="170"></BodyRiskMap>
                    </div>
                </div>
            </div>

            <div v-else class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400">
                Nenhuma matriz de risco encontrada para este paciente.
            </div>
        </section>

        <section id="files-section" v-if="!loading" class="scroll-mt-6 rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
            <div>
                <h2 class="text-xl font-bold text-gray-800">Arquivos</h2>
                <p class="mt-1 text-sm text-gray-500">Linha do tempo com exames, anexos e documentos complementares.</p>
            </div>

            <div class="mt-5">
                <input ref="fileInput" type="file" multiple class="hidden" @change="onFileInputChange" />
                <div
                    class="w-full cursor-pointer rounded-[28px] border border-dashed px-4 py-7 transition-all sm:px-6 sm:py-8"
                    :class="dragActive ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200 bg-slate-50/80 hover:border-cyan-300 hover:bg-cyan-50/60'"
                    @click="openFileDialog"
                    @dragenter.prevent="dragActive = true"
                    @dragover.prevent="dragActive = true"
                    @dragleave.prevent="handleDragLeave"
                    @drop.prevent="handleFileDrop"
                >
                    <div class="flex flex-col items-center justify-center text-center">
                        <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-cyan-600 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 16V8m0 0l-3 3m3-3l3 3M4 16.5A2.5 2.5 0 016.5 14H7a5 5 0 019.7-1.7A3.5 3.5 0 0118 19H6.5A2.5 2.5 0 014 16.5Z" />
                            </svg>
                        </div>
                        <div class="mt-4 text-base font-semibold text-slate-700">Arraste arquivos aqui ou clique para enviar</div>
                        <div class="mt-2 max-w-2xl text-sm leading-6 text-slate-400">Uploads entram na pasta do paciente e aparecem logo abaixo nesta timeline.</div>
                        <div v-if="uploadingFiles" class="mt-5 w-full max-w-xl">
                            <div class="h-2 overflow-hidden rounded-full bg-cyan-100">
                                <div class="h-full rounded-full bg-cyan-500 transition-all" :style="{ width: `${uploadProgress}%` }"></div>
                            </div>
                            <div class="mt-2 text-xs font-semibold text-cyan-700">Enviando {{ uploadProgress }}%</div>
                        </div>
                    </div>
                </div>
            </div>

            <FileTable
                v-if="patient.files && patient.files.length"
                class="mt-6"
                :files="sortedFiles"
                :loading="loadingFiles"
                timeline
                @remove-file="removeFile"
            />

            <div v-else class="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400">
                Nenhum arquivo anexado ainda.
            </div>
        </section>

        <section id="evolution-section" v-if="!loading" class="scroll-mt-6 rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
            <div>
                <h2 class="text-xl font-bold text-gray-800">Evolução</h2>
                <p class="mt-1 text-sm text-gray-500">Registre cada atendimento em uma linha do tempo clínica.</p>
            </div>

            <form class="mt-5 rounded-[24px] border border-cyan-100 bg-cyan-50/40 p-4 sm:p-5" @submit.prevent="saveEvolution">
                <div class="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)]">
                    <label class="block">
                        <span class="text-xs font-bold uppercase tracking-wide text-slate-600">Data</span>
                        <Datepicker
                            v-model="evolutionDate"
                            class="mt-2 w-full"
                            :clearable="false"
                            locale="pt-BR"
                            :enableTimePicker="false"
                            format="dd/MM/yyyy"
                            autoApply
                        />
                    </label>
                    <label class="block">
                        <span class="text-xs font-bold uppercase tracking-wide text-slate-600">Evolução clínica</span>
                        <textarea v-model="evolutionContent" rows="4" required placeholder="Descreva a evolução do paciente..." class="mt-2 block w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm leading-6 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"></textarea>
                    </label>
                </div>

                <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex min-w-0 flex-wrap items-center gap-2">
                        <input ref="evolutionFileInput" type="file" multiple class="hidden" @change="onEvolutionFileInputChange" />
                        <button type="button" class="inline-flex items-center gap-2 rounded-xl border border-cyan-200 bg-white px-4 py-2.5 text-sm font-semibold text-cyan-700 transition hover:border-cyan-400 hover:bg-cyan-50" @click="openEvolutionFileDialog">
                            Adicionar arquivos
                        </button>
                        <span v-if="evolutionFiles.length" class="text-xs font-semibold text-slate-500">{{ evolutionFiles.length }} arquivo(s) selecionado(s)</span>
                    </div>
                    <button type="submit" class="btn btn-primary w-full sm:w-auto" :disabled="savingEvolution">
                        {{ savingEvolution ? 'Salvando...' : 'Salvar evolução' }}
                    </button>
                </div>

                <div v-if="evolutionFiles.length" class="mt-3 flex flex-wrap gap-2">
                    <span v-for="(file, index) in evolutionFiles" :key="file.name + index" class="inline-flex max-w-full items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs text-slate-600 ring-1 ring-slate-200">
                        <span class="max-w-[220px] truncate">{{ file.name }}</span>
                        <button type="button" class="font-bold text-slate-400 hover:text-rose-500" @click="removeEvolutionFile(index)">×</button>
                    </span>
                </div>
            </form>

            <div v-if="loadingEvolutions" class="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                Carregando evoluções...
            </div>

            <ol v-else-if="sortedEvolutions.length" class="mt-6 space-y-4">
                <li v-for="(evolution, index) in sortedEvolutions" :key="evolution.uuid || evolution.id" class="relative pl-8">
                    <span class="absolute left-0 top-5 z-10 flex h-4 w-4 items-center justify-center rounded-full border-4 border-white bg-cyan-500 shadow-sm"></span>
                    <span v-if="index < sortedEvolutions.length - 1" class="absolute left-[7px] top-9 h-[calc(100%+1rem)] w-0.5 bg-cyan-100"></span>
                    <article class="rounded-[22px] border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
                        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <time class="text-sm font-bold text-cyan-700">{{ formatEvolutionDate(evolution.evolution_date) }}</time>
                            <span v-if="evolution.created_at" class="text-xs text-slate-400">Registrado em {{ formatDateTime(evolution.created_at) }}</span>
                        </div>
                        <p class="mt-3 whitespace-pre-line break-words text-sm leading-7 text-slate-700">{{ evolution.content }}</p>
                        <div v-if="evolution.files && evolution.files.length" class="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                            <a v-for="file in evolution.files" :key="file.id" :href="file.url" target="_blank" rel="noopener noreferrer" class="inline-flex max-w-full items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-cyan-50 hover:text-cyan-700">
                                <span class="rounded bg-white px-1.5 py-0.5 uppercase text-[10px] text-cyan-700 ring-1 ring-slate-200">{{ file.description && file.description.split('.').pop() }}</span>
                                <span class="max-w-[240px] truncate">{{ file.exam_description || file.description }}</span>
                            </a>
                        </div>
                    </article>
                </li>
            </ol>

            <div v-else class="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400">
                Nenhuma evolução registrada ainda.
            </div>
        </section>

        <!-- Fora da tela: matriz da avaliação exportada, só para virar imagem no PDF.
             O deslocamento fica no palco externo — o nó capturado precisa continuar
             estático, senão o html-to-image devolve uma imagem em branco. -->
        <div v-if="exportMatrix.length" class="export-matrix-stage">
            <div id="export-matrix-container" class="inline-flex items-start gap-6 bg-white p-2">
                <RiskMatrix :data="exportMatrix"></RiskMatrix>
                <BodyRiskMap v-if="showBodyRiskMap" :data="exportMatrix" :width="170"></BodyRiskMap>
            </div>
        </div>

        <transition name="fade">
            <div v-if="reportBuilderOpen" class="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/45 backdrop-blur-[2px] sm:items-center sm:px-6">
                <div class="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]">
                    <div class="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-5 sm:px-6">
                        <div>
                            <h3 class="text-lg font-bold text-slate-800">Gerar Relatório da Avaliação</h3>
                            <p class="mt-1 text-sm text-slate-500">
                                <template v-if="reportTargetHasConclusion">A conclusão desta avaliação abre o documento, o relatório com os valores vem em seguida e os PDFs escolhidos entram no final.</template>
                                <template v-else>Escolha PDFs extras para anexar ao final do relatório Sirimmatrix em um único documento.</template>
                            </p>
                        </div>
                        <button
                            type="button"
                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                            @click="closeReportBuilder"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
                        <div class="flex flex-wrap items-center justify-between gap-2">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">PDFs anexos opcionais</div>
                            <span
                                class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                                :class="reportSelectedFileIds.length ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-400'"
                            >
                                {{ reportSelectedFileIds.length }} selecionado(s)
                            </span>
                        </div>

                        <div v-if="!reportPdfFiles.length" class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                            Este paciente não tem PDFs adicionais anexados. Você ainda pode gerar só o relatório do Sirimmatrix.
                        </div>

                        <template v-else>
                            <div class="relative">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                </svg>
                                <input
                                    v-model="reportFileSearch"
                                    type="text"
                                    placeholder="Buscar PDF por nome..."
                                    class="input input-bordered w-full rounded-2xl pl-11 text-sm"
                                />
                            </div>

                            <div class="max-h-72 space-y-2 overflow-y-auto pr-1">
                                <label
                                    v-for="file in reportFilteredPdfFiles"
                                    :key="file.id"
                                    class="flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition-colors"
                                    :class="reportSelectedFileIds.includes(file.id) ? 'border-cyan-300 bg-cyan-50/60' : 'border-slate-200 hover:border-cyan-200'"
                                >
                                    <input
                                        type="checkbox"
                                        class="checkbox checkbox-sm checkbox-primary"
                                        :checked="reportSelectedFileIds.includes(file.id)"
                                        :disabled="!reportSelectedFileIds.includes(file.id) && reportSelectedFileIds.length >= reportMaxFiles"
                                        @change="toggleReportFile(file.id)"
                                    />
                                    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[10px] font-bold uppercase text-slate-500">
                                        PDF
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <div class="truncate text-sm font-semibold text-slate-700">{{ file.exam_description || file.description }}</div>
                                        <div class="text-xs text-slate-400">{{ formatDate(file.created_at) }}</div>
                                    </div>
                                </label>

                                <div v-if="!reportFilteredPdfFiles.length" class="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-400">
                                    Nenhum PDF encontrado para "{{ reportFileSearch }}".
                                </div>
                            </div>

                            <p v-if="reportSelectedFileIds.length >= reportMaxFiles" class="text-xs font-semibold text-slate-500">
                                Limite de {{ reportMaxFiles }} PDFs por relatório atingido.
                            </p>
                        </template>
                    </div>

                    <div class="flex flex-col gap-2 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                        <div class="text-xs text-slate-400">
                            <template v-if="reportTargetHasConclusion">Conclusão + relatório Sirimmatrix + {{ reportSelectedFileIds.length }} PDF(s) anexo(s)</template>
                            <template v-else>Relatório Sirimmatrix + {{ reportSelectedFileIds.length }} PDF(s) anexo(s)</template>
                        </div>
                        <div class="flex gap-2">
                            <button type="button" class="btn btn-outline btn-sm flex-1 sm:flex-none" @click="closeReportBuilder">
                                {{ loadingReport ? 'Cancelar geração' : 'Cancelar' }}
                            </button>
                            <button type="button" class="btn btn-primary btn-sm flex-1 sm:flex-none" :disabled="loadingReport || !reportTargetRecordId" @click="submitReportBuilder">
                                {{ loadingReport ? 'Gerando...' : 'Gerar PDF único' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div v-if="deleteModalOpen" class="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/55 px-4 backdrop-blur-[2px] sm:items-center">
                <div class="w-full max-w-lg rounded-[28px] bg-white shadow-2xl">
                    <div class="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                        <div>
                            <h3 class="text-lg font-bold text-slate-800">Excluir paciente</h3>
                            <p class="mt-1 text-sm text-slate-500">Esta ação remove o paciente e todos os dados vinculados do uso diário da plataforma.</p>
                        </div>
                        <button
                            type="button"
                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                            :disabled="deletingPatient"
                            @click="closeDeleteModal"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="space-y-4 px-6 py-5">
                        <div class="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4 text-sm leading-6 text-rose-800">
                            Você está prestes a excluir <strong>{{ patient.name }}</strong>. Todos os dados do paciente serão excluídos:
                            avaliações, matrizes, conclusões, ACIA e arquivos.
                        </div>

                        <div>
                            <label for="delete-confirmation" class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                Confirmação obrigatória
                            </label>
                            <p class="mt-1 text-sm text-slate-500">Digite <strong>excluir</strong> para liberar o botão final.</p>
                            <input
                                id="delete-confirmation"
                                v-model="deleteConfirmationText"
                                type="text"
                                autocomplete="off"
                                class="input input-bordered mt-3 w-full rounded-2xl"
                                placeholder="excluir"
                            />
                        </div>
                    </div>

                    <div class="flex flex-col gap-2 border-t border-slate-100 px-6 py-4 sm:flex-row sm:justify-end">
                        <button type="button" class="btn btn-outline btn-sm" :disabled="deletingPatient" @click="closeDeleteModal">Cancelar</button>
                        <button
                            type="button"
                            class="btn btn-sm border-rose-200 bg-rose-600 text-white hover:border-rose-700 hover:bg-rose-700 disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
                            :disabled="!canConfirmDelete"
                            @click="deletePatient"
                        >
                            {{ deletingPatient ? 'Excluindo...' : 'Excluir paciente agora' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue';
import UploadService from '../services/FileService';
import { PatientService, PatientEvolutionService, RecordService, ReportService, MatrixService, ConclusionService, UsageService } from '../services/Entity';
import AuthService from '../services/AuthService';
import { useRoute, useRouter } from 'vue-router';
import { defaultDate } from '../filters/date';
import Swal from 'sweetalert';
import { DateTime } from 'luxon';
import PaginationV2 from '../components/PaginationV2.vue';
import RiskMatrix from '../components/RiskMatrix.vue';
import BodyRiskMap from '../components/BodyRiskMap.vue';
import { classifyRiskBand, RISK_BAND_COLORS } from '../utils/risk';
import { captureMatrixPng } from '../utils/matrixCapture';
import Loading from '@/components/Loading';
import ConclusionGenerationOverlay from '@/components/ConclusionGenerationOverlay.vue';
import AciasSection from '@/components/AciasSection.vue';
import FileTable from '@/components/FileTable.vue';
import { PLANS } from '@/constants/plans';
import { SHOW_BODY_RISK_MAP } from '@/constants/features';
import { unwrapAiHtml } from '@shared/ai';

const REPORT_MAX_FILES = 10;
const EXPORT_MATRIX_ELEMENT_ID = 'export-matrix-container';
const RECORD_SHORTCUTS = [
    { id: 'patient-section', label: 'Paciente', description: 'Dados cadastrais' },
    { id: 'acias-section', label: 'ACIA', description: 'Análises clínicas' },
    { id: 'conclusion-section', label: 'Conclusão', description: 'Resumo IA' },
    { id: 'records-section', label: 'Avaliações', description: 'Histórico' },
    { id: 'risk-matrix-section', label: 'Matriz', description: 'Riscos' },
    { id: 'files-section', label: 'Arquivos', description: 'Exames e anexos' },
    { id: 'evolution-section', label: 'Evolução', description: 'Linha evolutiva' }
];

function getTodayDate() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDateForApi(value) {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return date.getFullYear() + '-' + month + '-' + day;
}

export default {
    components: {
        PaginationV2,
        RiskMatrix,
        BodyRiskMap,
        Loading,
        ConclusionGenerationOverlay,
        AciasSection,
        FileTable
    },
    setup() {
        const showBodyRiskMap = SHOW_BODY_RISK_MAP;
        const loading = ref(false);
        const route = useRoute();
        const router = useRouter();
        const page = ref(1);
        const limit = ref(10);
        const records = ref([]);
        const totalRecords = ref(0);
        const loadingReport = ref(false);
        const loadingFiles = ref(false);
        const uploadingFiles = ref(false);
        const uploadProgress = ref(0);
        const dragActive = ref(false);
        const matrixLoading = ref(true);
        const fileInput = ref(null);
        const evolutionFileInput = ref(null);
        const evolutionDate = ref(getTodayDate());
        const evolutionContent = ref('');
        const evolutionFiles = ref([]);
        const evolutions = ref([]);
        const loadingEvolutions = ref(false);
        const savingEvolution = ref(false);
        const matrix = ref([]);
        const exportMatrix = ref([]);
        const recentRecord = ref(null);
        const recentRecordMatrix = ref([]);
        const latestConclusion = ref(null);
        const loadingConclusion = ref(true);
        const exportingConclusionPdf = ref(false);
        const recordConclusions = ref({});
        const loadingConclusionRecordUuid = ref('');
        const usageInfo = ref(null);
        const patient = ref({ files: [] });
        const highestPlanId = PLANS[PLANS.length - 1]?.id || 'standard';
        const deleteModalOpen = ref(false);
        const deleteConfirmationText = ref('');
        const deletingPatient = ref(false);
        const reportBuilderOpen = ref(false);
        const reportTargetRecordId = ref(null);
        const reportAbortController = ref(null);
        const reportSelectedFileIds = ref([]);
        const reportFileSearch = ref('');
        const reportMaxFiles = REPORT_MAX_FILES;

        const sortedFiles = computed(() => {
            return [...(patient.value.files || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });

        const sortedEvolutions = computed(() => {
            return [...evolutions.value].sort((a, b) => {
                const dateComparison = String(b.evolution_date || '').localeCompare(String(a.evolution_date || ''));
                return dateComparison || Number(b.id || 0) - Number(a.id || 0);
            });
        });

        const reportPdfFiles = computed(() => {
            return [...(patient.value.files || [])]
                .filter((file) => normalizeMimeType(file && file.type) === 'application/pdf')
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });

        const reportFilteredPdfFiles = computed(() => {
            const query = reportFileSearch.value.trim().toLowerCase();
            if (!query) return reportPdfFiles.value;

            return reportPdfFiles.value.filter((file) => {
                const name = `${file.exam_description || ''} ${file.description || ''}`.toLowerCase();
                return name.includes(query);
            });
        });

        // Conclusão da avaliação escolhida abre o PDF, então o modal avisa antes de gerar.
        const reportTargetHasConclusion = computed(() => {
            const target = records.value.find((record) => record.id === reportTargetRecordId.value);
            const conclusion = target && target.uuid ? recordConclusions.value[target.uuid] : null;

            return Boolean(conclusion && conclusion.status === 'done');
        });

        const canConfirmDelete = computed(() => {
            return deleteConfirmationText.value.trim() === 'excluir' && !deletingPatient.value;
        });

        // Preview enxuto: só os primeiros caracteres — o conteúdo completo fica na página dedicada
        const conclusionPreview = computed(() => {
            const html = unwrapAiHtml(latestConclusion.value?.text_content);
            const text = html
                .replace(/<[^>]*>/g, ' ')
                .replace(/&nbsp;/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!text) return 'Conclusão gerada — abra a página dedicada para ler o relatório completo.';
            return text.length > 320 ? `${text.slice(0, 320).trim()}…` : text;
        });

        const riskProfileChart = computed(() => {
            const matrixData = Array.isArray(recentRecordMatrix.value) ? recentRecordMatrix.value : [];
            const counts = Object.fromEntries(
                Object.entries(RISK_BAND_COLORS).map(([band, color]) => [band, { value: 0, color }])
            );

            matrixData.forEach((item) => {
                const band = classifyRiskBand(item);
                counts[band].value += 1;
            });

            const max = Math.max(1, ...Object.values(counts).map((item) => item.value));
            return Object.entries(counts).map(([label, item]) => ({
                label,
                value: item.value,
                width: (item.value / max) * 100,
                color: item.color
            }));
        });

        const probabilityChart = computed(() => {
            const matrixData = Array.isArray(recentRecordMatrix.value) ? recentRecordMatrix.value : [];
            const counts = { Baixa: 0, Média: 0, Alta: 0, 'Muito alta': 0 };

            matrixData.forEach((item) => {
                if (item.probability === 'low') counts.Baixa += 1;
                if (item.probability === 'medium') counts.Média += 1;
                if (item.probability === 'high') counts.Alta += 1;
                if (item.probability === 'very_high') counts['Muito alta'] += 1;
            });

            return Object.entries(counts).map(([label, value]) => ({ label, value }));
        });

        onMounted(async () => {
            loading.value = true;
            const patientId = handleRoute(route);

            if (!patientId) {
                router.push({ name: 'patients' });
                return;
            }

            await getPatient(patientId);
            await loadEvolutions(patientId);
            const items = await listRecords(patientId);
            await Promise.all([
                loadMatrix(patientId),
                loadUsage(),
                loadRecordConclusions(items)
            ]);
            await loadLatestConclusion(items);

            loading.value = false;
        });

        function handleRoute(route) {
            const patientId = route.params.uuid;

            return patientId ? patientId : false;
        }

        async function loadMatrix(patientUUID) {
            matrixLoading.value = true;
            try {
                const { data } = await MatrixService.getMostRecentMatrixFromPatient(patientUUID);
                matrix.value = (data && data.info) || [];
            } catch (err) {
                matrix.value = [];
            } finally {
                matrixLoading.value = false;
            }
        }

        async function listRecords(patientUUID) {
            try {
                loading.value = true;
                const { data: { items, total } } = await RecordService.listByPatientUUID(page.value, limit.value, patientUUID);

                records.value = items;
                totalRecords.value = total;
                loading.value = false;
                return items;
            } catch(err) {
                loading.value = false;

                Swal({
                    title: 'Ocorreu um problema ao listar as avaliações do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
                return [];
            }
        }

        function sortConclusions(conclusions = []) {
            return [...conclusions].sort((a, b) => new Date(b.generated_at || b.updated_at || b.created_at || 0) - new Date(a.generated_at || a.updated_at || a.created_at || 0));
        }

        function pickLatestConclusion(conclusions = []) {
            return sortConclusions(conclusions)[0] || null;
        }

        async function loadRecordConclusions(items = []) {
            const nextMap = {};

            await Promise.all((items || []).map(async (record) => {
                if (!record?.uuid) return;

                try {
                    const { data } = await ConclusionService.getByRecord(record.uuid);
                    nextMap[record.uuid] = pickLatestConclusion((data && data.data) || []);
                } catch (err) {
                    nextMap[record.uuid] = null;
                }
            }));

            recordConclusions.value = {
                ...recordConclusions.value,
                ...nextMap
            };
        }

        async function loadUsage() {
            try {
                const { data } = await UsageService.getUsage();
                usageInfo.value = (data && data.data) || null;
                return usageInfo.value;
            } catch (err) {
                return usageInfo.value;
            }
        }

        async function loadLatestConclusion(items = []) {
            loadingConclusion.value = true;
            recentRecord.value = Array.isArray(items) && items.length ? items[0] : null;
            latestConclusion.value = null;
            recentRecordMatrix.value = [];

            if (!recentRecord.value) {
                loadingConclusion.value = false;
                return;
            }

            try {
                // Sem fallback para a matriz do paciente: os graficos abaixo sao da avaliacao
                // mais recente, e preenche-los com a matriz de outra avaliacao mostra numero
                // que nao pertence a este registro.
                if (recentRecord.value.matrix_uuid) {
                    const { data } = await MatrixService.getByUUID(recentRecord.value.matrix_uuid);
                    recentRecordMatrix.value = (data && data.info) || [];
                }
            } catch (err) {
                recentRecordMatrix.value = [];
            }

            try {
                const { data } = await ConclusionService.getByRecord(recentRecord.value.uuid);
                const conclusions = (data && data.data) || [];

                latestConclusion.value = pickLatestConclusion(conclusions);
                recordConclusions.value = {
                    ...recordConclusions.value,
                    [recentRecord.value.uuid]: latestConclusion.value
                };
            } catch (err) {
                latestConclusion.value = null;
            } finally {
                loadingConclusion.value = false;
            }
        }

        async function getPatient(patientUUID, { silent = false } = {}) {
            try {
                if (!silent) {
                    loading.value = true;
                }

                const { data } = await PatientService.getByUUID(patientUUID);
                patient.value = data;

                if (!silent) {
                    loading.value = false;
                }
            } catch(err) {
                if (!silent) {
                    loading.value = false;
                }

                if (err?.response?.status === 404) {
                    await Swal({
                        title: 'Paciente não encontrado',
                        text: 'Este prontuário não está mais disponível.',
                        icon: 'warning'
                    });
                    router.push({ name: 'patients' });
                    return;
                }

                Swal({
                    title: 'Ocorreu um problema ao consultar os dados do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function loadEvolutions(patientUUID) {
            if (!patientUUID) return;

            loadingEvolutions.value = true;
            try {
                const { data } = await PatientEvolutionService.listByPatientUuid(patientUUID);
                evolutions.value = Array.isArray(data) ? data : ((data && data.data) || []);
            } catch (err) {
                evolutions.value = [];
                Swal({
                    title: 'Ocorreu um problema ao listar as evoluções',
                    text: 'Atualize a página e tente novamente.',
                    icon: 'error'
                });
            } finally {
                loadingEvolutions.value = false;
            }
        }

        function openEvolutionFileDialog() {
            if (!savingEvolution.value) evolutionFileInput.value?.click();
        }

        function onEvolutionFileInputChange(event) {
            const files = event.target.files;
            if (files && files.length) {
                evolutionFiles.value = [...evolutionFiles.value, ...Array.from(files)];
            }
            event.target.value = '';
        }

        function removeEvolutionFile(index) {
            evolutionFiles.value.splice(index, 1);
        }

        async function saveEvolution() {
            if (!patient.value.uuid || !evolutionDate.value || !evolutionContent.value.trim()) {
                Swal({
                    title: 'Preencha a evolução',
                    text: 'Informe a data e o texto do atendimento.',
                    icon: 'warning'
                });
                return;
            }

            savingEvolution.value = true;
            const failedFiles = [];

            try {
                const { data } = await PatientEvolutionService.create(patient.value.uuid, {
                    evolution_date: formatDateForApi(evolutionDate.value),
                    content: evolutionContent.value.trim()
                });
                const evolution = data && data.data ? data.data : data;

                for (const file of evolutionFiles.value) {
                    try {
                        const response = await UploadService.upload(file, {
                            patientUuid: patient.value.uuid,
                            evolutionUuid: evolution.uuid,
                            folderType: 'evolucoes'
                        });
                        if (!response.data || response.data.err || !(response.data.success || []).length) {
                            failedFiles.push(file.name);
                        }
                    } catch (err) {
                        failedFiles.push(file.name);
                    }
                }

                evolutionDate.value = getTodayDate();
                evolutionContent.value = '';
                evolutionFiles.value = [];
                await Promise.all([
                    loadEvolutions(patient.value.uuid),
                    getPatient(patient.value.uuid, { silent: true })
                ]);

                if (failedFiles.length) {
                    Swal({
                        title: 'Evolução salva, mas alguns arquivos falharam',
                        text: failedFiles.join(', '),
                        icon: 'warning'
                    });
                }
            } catch (err) {
                Swal({
                    title: 'Não foi possível salvar a evolução',
                    text: 'Atualize a página e tente novamente.',
                    icon: 'error'
                });
            } finally {
                savingEvolution.value = false;
            }
        }

        function calculateAge(birthdate) {
            const diff = DateTime.fromISO(birthdate, 'yyyy-MM-dd').diffNow('year');
            return Math.trunc(Math.abs(diff.years));
        }

        async function changePagination(currentPage) {
            page.value = currentPage;
            const items = await listRecords(patient.value.uuid);
            await loadRecordConclusions(items);
        }

        function goToAvaliation(record) {
            if (!record || !record.id) {
                return;
            }

            router.push({ name: 'avaliation', params: { patient_uuid: patient.value.uuid, record_uuid: record.uuid } });
        }

        function openMatrix(record) {
            if (!record || !record.id) {
                return;
            }

            router.push({
                name: 'matrix',
                params: {
                    record_uuid: record.uuid,
                    patient_uuid: patient.value.uuid,
                    matrix_uuid: record.matrix_uuid
                },
                query: { record_id: record.id }
            });
        }

        function goToConclusion(record) {
            if (!record || !record.uuid || !patient.value.uuid) {
                return;
            }

            router.push({
                name: 'recordConclusion',
                params: {
                    patient_uuid: patient.value.uuid,
                    record_uuid: record.uuid
                }
            });
        }

        async function exportConclusionPdf() {
            if (!latestConclusion.value?.uuid) return;

            try {
                exportingConclusionPdf.value = true;

                await loadExportMatrix(recentRecord.value);
                const imageBase64 = await captureMatrixPng(EXPORT_MATRIX_ELEMENT_ID);
                const { data } = await ConclusionService.exportPdf(latestConclusion.value.uuid, imageBase64);
                const pdfKey = data?.data?.pdf_key;

                if (pdfKey) {
                    latestConclusion.value = { ...latestConclusion.value, pdf_url: data?.data?.pdf_url };
                }

                const downloadUrl = ConclusionService.downloadPdfUrl(latestConclusion.value.uuid);
                const auth = AuthService.getAuthInfo();
                const blobRes = await fetch(downloadUrl, {
                    headers: { 'Authorization': auth && auth.token ? auth.token : '' }
                });
                const blob = await blobRes.blob();
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `conclusao-${Date.now()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao exportar o PDF',
                    text: 'Tente novamente em instantes. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                exportingConclusionPdf.value = false;
            }
        }

        function openDeleteModal() {
            deleteConfirmationText.value = '';
            deleteModalOpen.value = true;
        }

        function closeDeleteModal() {
            if (deletingPatient.value) return;
            deleteModalOpen.value = false;
            deleteConfirmationText.value = '';
        }

        async function deletePatient() {
            if (!canConfirmDelete.value || !patient.value?.uuid) {
                return;
            }

            try {
                deletingPatient.value = true;
                await PatientService.destroy(patient.value.uuid);
                deleteModalOpen.value = false;
                deleteConfirmationText.value = '';
                await router.push({ name: 'patients' });
                await Swal({
                    title: 'Paciente excluído',
                    text: 'Paciente e dados clínicos vinculados foram removidos da rotina ativa da plataforma.',
                    icon: 'success'
                });
            } catch (err) {
                await Swal({
                    title: 'Falha ao excluir paciente',
                    text: 'Atualize a página e tente novamente. Se o problema persistir, fale com o suporte.',
                    icon: 'error'
                });
            } finally {
                deletingPatient.value = false;
            }
        }

        async function showUsageLimitModal() {
            const usage = usageInfo.value || await loadUsage();

            if (usage && usage.plan && usage.plan !== highestPlanId) {
                const shouldUpgrade = await Swal({
                    title: 'Limite de IA atingido',
                    text: 'Seu plano atual não possui mais créditos para gerar conclusões com IA. Faça upgrade para continuar.',
                    icon: 'warning',
                    buttons: {
                        cancel: 'Agora não',
                        confirm: {
                            text: 'Ver planos',
                            value: true
                        }
                    }
                });

                if (shouldUpgrade) {
                    router.push({ name: 'subscription' });
                }
                return;
            }

            await Swal({
                title: 'Sem créditos disponíveis',
                text: 'Seu plano atual já é o maior disponível, mas os créditos de IA deste ciclo já foram consumidos.',
                icon: 'warning'
            });
        }

        async function generateConclusion(record) {
            if (!record?.uuid) {
                return;
            }

            try {
                loadingConclusionRecordUuid.value = record.uuid;
                const { data } = await ConclusionService.generate(record.uuid);
                const generatedConclusion = (data && data.data) || null;

                recordConclusions.value = {
                    ...recordConclusions.value,
                    [record.uuid]: generatedConclusion
                };

                if (recentRecord.value?.uuid === record.uuid) {
                    latestConclusion.value = generatedConclusion;
                }

                await loadUsage();
                goToConclusion(record);
            } catch (err) {
                const errorCode = err?.response?.data?.error;

                if (errorCode === 'usage_limit_reached') {
                    await showUsageLimitModal();
                    return;
                }

                if (errorCode === 'openrouter_not_configured') {
                    await Swal({
                        title: 'IA indisponível',
                        text: 'A integração com OpenRouter ainda não está configurada neste ambiente.',
                        icon: 'warning'
                    });
                    return;
                }

                if (errorCode === 'openrouter_failed') {
                    await Swal({
                        title: 'Falha ao gerar conclusão',
                        text: 'A IA não conseguiu responder agora. Tente novamente em instantes.',
                        icon: 'error'
                    });
                    return;
                }

                await Swal({
                    title: 'Ocorreu um problema ao gerar a conclusão',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                loadingConclusionRecordUuid.value = '';
            }
        }

        async function handleConclusionAction(record) {
            if (!record?.uuid) {
                return;
            }

            if (recordConclusions.value[record.uuid]) {
                goToConclusion(record);
                return;
            }

            await generateConclusion(record);
        }

        function shouldHighlightConclusion(record) {
            return Boolean(record?.uuid) && !recordConclusions.value[record.uuid] && loadingConclusionRecordUuid.value !== record.uuid;
        }

        function conclusionSummaryButtonClass(record) {
            return shouldHighlightConclusion(record) ? 'record-ai-cta' : 'btn-primary';
        }

        function conclusionActionButtonClass(record) {
            return shouldHighlightConclusion(record)
                ? 'record-ai-cta'
                : 'border-emerald-500 bg-white text-emerald-600 hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 disabled:border-slate-200 disabled:bg-white disabled:text-slate-400';
        }

        function hasRiskMatrix(record) {
            return Boolean(record && record.matrix_uuid);
        }

        function matrixBadgeLabel(record) {
            return hasRiskMatrix(record) ? 'Matriz vinculada' : 'Sem matriz de risco';
        }

        function matrixBadgeClass(record) {
            return hasRiskMatrix(record)
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
        }

        function matrixActionLabel(record) {
            return hasRiskMatrix(record) ? 'Ver matriz' : 'Criar matriz';
        }

        function matrixActionButtonClass(record) {
            return hasRiskMatrix(record)
                ? 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-700'
                : 'border-amber-500 bg-amber-500 text-white hover:border-amber-600 hover:bg-amber-600 disabled:border-amber-300 disabled:bg-amber-300';
        }

        function recordCardClass(record) {
            return shouldHighlightConclusion(record)
                ? 'record-ai-card'
                : 'border-slate-100 hover:border-cyan-200 hover:bg-slate-50';
        }

        function normalizeMimeType(type) {
            return String(type || '')
                .split(';')[0]
                .trim()
                .toLowerCase();
        }

        function openReportBuilder(recordId) {
            if (!recordId || loadingReport.value) return;

            reportTargetRecordId.value = recordId;
            reportSelectedFileIds.value = [];
            reportFileSearch.value = '';
            reportBuilderOpen.value = true;
        }

        // Fechar durante a geração aborta a request em vez de ficar refém dela: antes o
        // modal simplesmente não respondia enquanto `loadingReport` estivesse ligado, e
        // uma request pendurada deixava a tela inutilizável até recarregar o navegador.
        function closeReportBuilder(force = false) {
            if (loadingReport.value && !force) {
                reportAbortController.value?.abort();
            }

            reportBuilderOpen.value = false;
            reportTargetRecordId.value = null;
            reportSelectedFileIds.value = [];
            reportFileSearch.value = '';
        }

        function toggleReportFile(fileId) {
            if (reportSelectedFileIds.value.includes(fileId)) {
                reportSelectedFileIds.value = reportSelectedFileIds.value.filter((id) => id !== fileId);
                return;
            }

            if (reportSelectedFileIds.value.length >= reportMaxFiles) {
                return;
            }

            reportSelectedFileIds.value = [...reportSelectedFileIds.value, fileId];
        }

        async function submitReportBuilder() {
            if (!reportTargetRecordId.value) return;
            await generateReport(reportTargetRecordId.value, reportSelectedFileIds.value);
        }

        // A matriz do PDF é sempre a da própria avaliação exportada. Sem matriz
        // vinculada o documento sai sem o mapa corporal: cair para a matriz mais
        // recente do paciente colocava a imagem de OUTRA avaliação ao lado de um texto
        // que o backend gera só com a matriz do record — documento desencontrado.
        async function loadExportMatrix(record) {
            exportMatrix.value = [];

            if (record && record.matrix_uuid) {
                try {
                    const { data } = await MatrixService.getByUUID(record.matrix_uuid);
                    exportMatrix.value = (data && data.info) || [];
                } catch (err) {
                    exportMatrix.value = [];
                }
            }

            await nextTick();
            return exportMatrix.value;
        }

        async function generateReport(recordId, fileIds = []) {
             const abortController = new AbortController();
             reportAbortController.value = abortController;

             try {
                loadingReport.value = true;

                await loadExportMatrix(records.value.find((record) => record.id === recordId));
                const imageBase64 = await captureMatrixPng(EXPORT_MATRIX_ELEMENT_ID);

                const payload = {
                    record_id: recordId,
                    image: imageBase64,
                    file_ids: fileIds,
                };

                const response = await ReportService.avaliationReport(payload, abortController.signal);
                const reportData = response.data;
                const downloadKey = reportData && reportData.key ? reportData.key : null;

                // Sem chave não há o que baixar: fechar o modal aqui daria a impressão de
                // sucesso com o download que nunca vem.
                if (!downloadKey) {
                    throw new Error('report_without_download_key');
                }

                const auth = AuthService.getAuthInfo();
                const downloadUrl = ReportService.downloadReportUrl(downloadKey);
                const blobRes = await fetch(downloadUrl, {
                    headers: { 'Authorization': auth && auth.token ? auth.token : '' },
                    signal: abortController.signal
                });

                if (!blobRes.ok) {
                    throw new Error('report_download_failed');
                }

                const blob = await blobRes.blob();
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `relatorio-avaliacao-${Date.now()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                // Só fecha com o download disparado — é o sinal de que deu certo.
                closeReportBuilder(true);
            } catch(err) {
                // Cancelamento é ação do próprio usuário: fechar o modal já é o feedback.
                if (abortController.signal.aborted) {
                    return;
                }

                const timedOut = err?.code === 'ECONNABORTED';

                // O modal continua aberto de propósito: o fisioterapeuta lê o erro com a
                // seleção de PDFs preservada e tenta de novo sem refazer nada.
                Swal({
                    title: timedOut
                        ? 'A geração do relatório demorou demais'
                        : 'Ocorreu um problema ao gerar o relatório',
                    text: timedOut
                        ? 'O servidor não respondeu a tempo. Tente novamente com menos PDFs anexos, se o erro persistir entre em contato com o suporte'
                        : 'Tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            } finally {
                loadingReport.value = false;

                if (reportAbortController.value === abortController) {
                    reportAbortController.value = null;
                }
            }
        }

        function openFileDialog() {
            if (uploadingFiles.value) return;
            fileInput.value?.click();
        }

        function onFileInputChange(event) {
            const files = event.target.files;
            if (!files || !files.length) return;
            uploadFiles(files);
            event.target.value = '';
        }

        function handleDragLeave(event) {
            if (event.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
                return;
            }
            dragActive.value = false;
        }

        function handleFileDrop(event) {
            dragActive.value = false;
            const files = event.dataTransfer?.files;
            if (!files || !files.length) return;
            uploadFiles(files);
        }

        async function uploadFiles(fileList) {
            if (!patient.value.uuid || !patient.value.id || !fileList || !fileList.length) {
                return;
            }

            const files = Array.from(fileList);
            const failedFiles = [];

            uploadingFiles.value = true;
            loadingFiles.value = true;
            uploadProgress.value = 0;

            for (let index = 0; index < files.length; index++) {
                const file = files[index];

                try {
                    const response = await UploadService.upload(
                        file,
                        { patientUuid: patient.value.uuid, folderType: 'arquivos' },
                        (event) => {
                            const fileProgress = event.total ? event.loaded / event.total : 0;
                            uploadProgress.value = Math.round(((index + fileProgress) / files.length) * 100);
                        }
                    );

                    const uploadedFile = response.data && response.data.success && response.data.success[0];
                    if (uploadedFile && uploadedFile.id) {
                        await PatientService.addFile(patient.value.id, uploadedFile.id);
                    } else {
                        failedFiles.push(file.name);
                    }
                } catch (err) {
                    failedFiles.push(file.name);
                }
            }

            await getPatient(patient.value.uuid, { silent: true });

            uploadingFiles.value = false;
            loadingFiles.value = false;
            uploadProgress.value = 0;

            if (failedFiles.length) {
                Swal({
                    title: 'Alguns arquivos não foram enviados',
                    text: failedFiles.join(', '),
                    icon: 'warning'
                });
            }
        }

        async function removeFile(fileId) {
            try {
                Swal({
                    title: 'Deseja realmente remover o arquivo?',
                    text: 'Não será possível restaurar o mesmo',
                    icon: 'warning',
                    buttons: {
                        cancel: 'Cancelar',
                        confirm: {
                            text: 'Deletar',
                            className: 'swal-button--danger'
                        }
                    }
                }).then(async (confirm) => {
                    if (!confirm) return;

                    loadingFiles.value = true;
                    const { data } = await PatientService.removeFile(patient.value.id, fileId, { patient: patient.value.id, file: fileId });
                    patient.value.files = data || [];
                    loadingFiles.value = false;
                });
            } catch (err) {
                loadingFiles.value = false;
                Swal({
                    title: 'Ocorreu um erro ao excluir o arquivo',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        function initials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ').filter(Boolean);
            if (!parts.length) return '?';
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        function modalityLabels(currentPatient) {
            return currentPatient && currentPatient.modality && currentPatient.modality.length
                ? currentPatient.modality.map((item) => item.label || item.name).filter(Boolean)
                : [];
        }

        function levelLabel(level) {
            if (level === 'high_professional') return 'Alto rendimento profissional';
            if (level === 'high_fun') return 'Alto rendimento recreacional';
            if (level === 'casual') return 'Atleta de fim de semana';
            return 'Nível não informado';
        }

        function genderLabel(gender) {
            if (gender === 'female') return 'Feminino';
            if (gender === 'male') return 'Masculino';
            return '—';
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatEvolutionDate(dateStr) {
            const value = String(dateStr || '').slice(0, 10);
            const parts = value.split('-');
            return parts.length === 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : '—';
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        function scrollToSection(id) {
            const target = document.getElementById(id);
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        return {
            RECORD_SHORTCUTS,
            showBodyRiskMap,
            page,
            limit,
            records,
            recentRecord,
            latestConclusion,
            loadingConclusion,
            exportingConclusionPdf,
            exportConclusionPdf,
            conclusionPreview,
            recordConclusions,
            riskProfileChart,
            probabilityChart,
            fileInput,
            evolutionFileInput,
            evolutionDate,
            evolutionContent,
            evolutionFiles,
            sortedEvolutions,
            loadingEvolutions,
            savingEvolution,
            dragActive,
            loadingReport,
            loadingConclusionRecordUuid,
            uploadingFiles,
            uploadProgress,
            loadingFiles,
            generateReport,
            totalRecords,
            sortedFiles,
            reportBuilderOpen,
            reportTargetRecordId,
            reportSelectedFileIds,
            reportFileSearch,
            reportMaxFiles,
            reportPdfFiles,
            reportFilteredPdfFiles,
            reportTargetHasConclusion,
            deleteModalOpen,
            deleteConfirmationText,
            deletingPatient,
            canConfirmDelete,
            goToAvaliation,
            goToConclusion,
            openMatrix,
            openDeleteModal,
            closeDeleteModal,
            deletePatient,
            openReportBuilder,
            closeReportBuilder,
            toggleReportFile,
            submitReportBuilder,
            shouldHighlightConclusion,
            conclusionSummaryButtonClass,
            conclusionActionButtonClass,
            matrixBadgeLabel,
            matrixBadgeClass,
            matrixActionLabel,
            matrixActionButtonClass,
            recordCardClass,
            handleConclusionAction,
            changePagination,
            calculateAge,
            defaultDate,
            loading,
            patient,
            matrix,
            exportMatrix,
            matrixLoading,
            removeFile,
            initials,
            modalityLabels,
            levelLabel,
            genderLabel,
            formatDate,
            formatDateTime,
            formatRelative,
            scrollToSection,
            openFileDialog,
            onFileInputChange,
            handleFileDrop,
            handleDragLeave,
            openEvolutionFileDialog,
            onEvolutionFileInputChange,
            removeEvolutionFile,
            saveEvolution,
            formatEvolutionDate
        }
    },
}
</script>

<style scoped>
/* Renderizado fora da área visível: serve só de fonte para o screenshot da matriz
   (display:none não é capturável pelo html-to-image). */
.export-matrix-stage {
    position: fixed;
    top: 0;
    left: -10000px;
    pointer-events: none;
}

.record-ai-card {
    border-color: rgba(103, 232, 249, 0.9);
    background: radial-gradient(circle at top right, rgba(236, 254, 255, 0.98), rgba(255, 255, 255, 1) 52%);
    box-shadow: 0 6px 16px rgba(8, 145, 178, 0.07);
}

.record-ai-card:hover {
    border-color: rgba(34, 211, 238, 1);
    box-shadow: 0 8px 20px rgba(8, 145, 178, 0.1);
}

.record-ai-cta {
    position: relative;
    overflow: hidden;
    border-color: rgba(8, 145, 178, 0.55) !important;
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 45%, #0e7490 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(8, 145, 178, 0.16);
}

.record-ai-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    animation: record-ai-shine 2s linear infinite;
}

.record-ai-cta:hover {
    border-color: rgba(14, 116, 144, 1) !important;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.14), 0 6px 14px rgba(8, 145, 178, 0.22);
}

.record-ai-cta:disabled::before,
.record-ai-cta:disabled {
    animation: none;
}

.record-ai-cta:disabled::before {
    display: none;
}

.record-ai-cta-icon {
    opacity: 0.95;
}

@keyframes record-ai-shine {
    0% { left: 100%; }
    100% { left: -60%; }
}
</style>
